(ns frontend.view.entity-viewers.enrichment
  (:require
   [common.util :as cu]
   [clojure.string :as string]
   [common.localization :as loc]
   [frontend.utils :as fu]
   [frontend.actions.issue-reporting :as issue-reporting]
   [reagent.core :as r]
   [frontend.logging :as logging]))

(defn localize-external-field
  [custom-ns field-kw]
  (let [localization-key (keyword custom-ns (name field-kw))
        translated (loc/tr localization-key)]
    (if-not (loc/has-translation? localization-key)
      (do
        (issue-reporting/*->localization-missing localization-key identity identity)
        (name field-kw))
      translated)))

(def clearbit-person-definitions
  [[:name :enrichment-value/map]
   [:employment :enrichment-value/map]
   [:email :enrichment-value/email]
   [:phone :enrichment-value/phone]
   [:timeZone :enrichment-value/timezone]
   [:utcOffset :enrichment-value/offset]
   [:bio :enrichment-value/string]
   [:linkedin :enrichment-value/url-handle-map]
   [:facebook :enrichment-value/url-handle-map]
   [:twitter :enrichment-value/url-handle-map]
   [:googleplus :enrichment-value/url-handle-map]
   [:github :enrichment-value/map]
   [:site :enrichment-value/string]
   [:geo :enrichment-value/map]
   [:id :enrichment-value/string]
   [:avatar :enrichment-value/logo]
   [:gravatar :enrichment-value/map]
   [:location :enrichment-value/string]
   [:indexedAt :enrichment-value/date-str]
   [:emailProvider :enrichment-value/hide]
   [:fuzzy :enrichment-value/hide]])

(def clearbit-person-field-order
  (->> clearbit-person-definitions
       (mapv first)))

(def clearbit-person-field-types
  (into {} clearbit-person-definitions))

(def clearbit-company-definitions
  [[:name :enrichment-value/string]
   [:description :enrichment-value/string]
   [:legalName :enrichment-value/string]
   [:ticker :enrichment-value/string]
   [:phone :enrichment-value/phone]
   [:foundedYear :enrichment-value/string]
   [:geo :enrichment-value/map]
   [:location :enrichment-value/string]
   [:domain :enrichment-value/string]
   [:site :enrichment-value/map]
   [:linkedin :enrichment-value/url-handle-map]
   [:facebook :enrichment-value/url-handle-map]
   [:twitter :enrichment-value/url-handle-map]
   [:crunchbase :enrichment-value/url-handle-map]
   [:category :enrichment-value/map]
   [:tags :enrichment-value/list-string]
   [:timeZone :enrichment-value/timezone]
   [:parent :enrichment-value/map]
   [:identifiers :enrichment-value/map]
   [:logo :enrichment-value/logo]
   [:type :enrichment-value/string]
   [:domainAliases :enrichment-value/list-string]
   [:ultimateParent :enrichment-value/map]
   [:id :enrichment-value/string]
   [:utcOffset :enrichment-value/offset]
   [:emailProvider :enrichment-value/none]
   [:techCategories :enrichment-value/list-string]
   [:tech :enrichment-value/list-string]
   [:metrics :enrichment-value/map]
   [:indexedAt :enrichment-value/date-str]])

(def clearbit-company-field-order
  (->> clearbit-company-definitions
       (mapv first)))

(def clearbit-company-field-types
  (into {} clearbit-company-definitions))

(def crunchbase-company-field-order
  [:name
   :short-description
   :domain
   :type
   :location-city
   :location-region
   :location-country-code
   :stock-symbol
   :primary-role
   :crunchbase-uuid
   :crunchbase-url
   :twitter-url
   :facebook-url
   :homepage-url
   :profile-image-url
   :linkedin-url])

(defn resolve-name-value-map
  [l-prefix nk nv]
  (when (map? nv)
    (let [non-blank-fields (->> nv
                                (keep (fn [[k v]]
                                        (when-not (cu/blank-value? v)
                                          [(localize-external-field (str l-prefix "." (name nk)) k)
                                           v])))
                                (seq))]
      (when non-blank-fields
        (into {} non-blank-fields)))))

(defn flatten-name-value-tree->table-data
  [label-prefix namespace-keyword nv sorted-column-keys sort-by-field formatters]
  (concat
   [(->> sorted-column-keys
         (mapv (fn [f]
                 (localize-external-field (str label-prefix "." (name namespace-keyword)) f))))]
   (->> nv
        (sort-by sort-by-field)
        (reverse)
        (filter map?)
        (mapv (fn [v]
                (->> sorted-column-keys
                     (mapv (fn [f]
                             (let [formatter (get formatters f)
                                   fv (get v f)]
                               (if (fn? formatter)
                                 (formatter fv)
                                 (str fv)))))))))))

(defn map->table-data
  [l-prefix nk nv]
  (let [all-fields (->> nv
                        (mapcat keys)
                        (distinct)
                        (sort))]
    (concat [(->> all-fields
                  (mapv (fn [f]
                          (localize-external-field (str l-prefix "." (name nk)) f))))]
            (->> nv
                 (filter map?)
                 (mapv (fn [v]
                         (->> all-fields
                              (mapv (fn [f]
                                      (str (get v f)))))))))))

(defn resolve-url-handle
  [v]
  (get v :handle))

(def border-style "1px solid black")

(defn str-value-row
  [label value dom-id]
  [:tr {:key dom-id}
   [:td.zap-table__cell.text-size--sm
    [:strong.text-bold label]]
   [:td.zap-table__cell.text-size--sm
    value]])

(defn hiccup-value-row
  [label value dom-id]
  [:tr {:key dom-id}
   [:td.zap-table__cell.text-size--sm
    [:strong.text-bold.d-inline-block.pr-2 label]]
   [:td.zap-table__cell.text-size--sm
    value]])

(defn table-values-row
  [label value dom-id]
  (let [local-state (r/atom {:collapsed? false})]
    (fn [label value dom-id]
      (let [collapsed? (:collapsed? @local-state)
            headings (first value)
            col-count (count headings)
            rows (rest value)]
        [:tr {:key dom-id}
         [:td {:col-span 2}
          [:table.zap-table.zap-table--bordered.w-100
           [:tbody
            [:tr
             [:td.zap-table__cell.bg-light.text-size--sm
              {:col-span col-count
               :on-click (fn [_]
                           (swap! local-state update :collapsed? not))}
              [:strong.text-bold.d-inline-block.pr-2.pointer label]
              (if collapsed?
                [:i.fal.fa-chevron-up]
                [:i.fal.fa-chevron-down])]]]
           [:tbody
            {:style {:display  (if collapsed? "block" "table")
                     :overflow "hidden"
                     :width    "100%"
                     :height   (if collapsed? 0 "auto")}}
            [:tr
             (->> headings
                  (map-indexed (fn [idx h]
                                 [:td.zap-table__cell.text-bold.text-size--sm
                                  {:key   (str dom-id "-heading-" h)
                                   :style {:background "#f8f8f8"
                                           :width      (if (= (inc idx) col-count)
                                                         "100%"
                                                         "auto")}}
                                  h]))
                  (doall))]
            (->> rows
                 (map-indexed (fn [rix row]
                                [:tr
                                 {:key (str dom-id "-row-" rix)}
                                 (->> row
                                      (map-indexed (fn [idx col]
                                                     [:td.zap-table__cell.text-size--sm
                                                      {:key   (str dom-id "-row-" rix "-col-" idx)
                                                       :style {:background "#f8f8f8"}}
                                                      col]))
                                      (doall))]))
                 (doall))]]]]))))

(defn map-values-row
  [label value dom-id]
  (let [local-state (r/atom {:collapsed? false})]
    (fn [label value dom-id]
      (let [collapsed? (:collapsed? @local-state)]
        [:tr {:key dom-id}
         [:td {:col-span 2}
          [:table.zap-table.zap-table--bordered.w-100
           [:tbody
            [:tr
             [:td.zap-table__cell.bg-light.text-size--sm
              {:col-span (count value)
               :on-click (fn [_]
                           (swap! local-state update :collapsed? not))}
              [:strong.text-bold.d-inline-block.pr-2.pointer label]
              (if collapsed?
                [:i.fal.fa-chevron-up]
                [:i.fal.fa-chevron-down])]]]
           [:tbody
            {:style {:display  (if collapsed? "block" "table")
                     :overflow "hidden"
                     :width    "100%"
                     :height   (if collapsed? 0 "auto")}}
            [:tr
             (->> value
                  (map-indexed (fn [idx [k _]]
                                 [:td.zap-table__cell.bg-light.text-bold.text-size--sm
                                  {:key (str dom-id "-" k "-" idx)}
                                  k]))
                  (doall))]
            [:tr
             (->> value
                  (map-indexed (fn [idx [k v]]
                                 [:td.zap-table__cell.bg-light.text-size--sm
                                  {:key (str dom-id "-" k "-" idx "-value")}
                                  (str v)]))
                  (doall))]]]]]))))

(defn localization-prefix
  [intelligence-entity]
  (let [source (:intelligence-data/source intelligence-entity)
        entity-type (:intelligence-data/entity-type intelligence-entity)]
    (str (name source) "-" (name entity-type))))

(defn resolve-value
  [intelligence-entity dom-id k v]
  (let [source (:intelligence-data/source intelligence-entity)
        entity-type (:intelligence-data/entity-type intelligence-entity)
        l-prefix (localization-prefix intelligence-entity)
        value-type (case source
                     :clearbit (if (= entity-type :entity.type/company)
                                 (get clearbit-company-field-types k :enrichment-value/string)
                                 (get clearbit-person-field-types k :enrichment-value/string))
                     :enrichment-value/string)]
    (case value-type
      :enrichment-value/string (fu/sanitize-text (str v))
      :enrichment-value/map (resolve-name-value-map l-prefix k v)
      :enrichment-value/list-confidence-country-headquarter (flatten-name-value-tree->table-data
                                                             l-prefix
                                                             k
                                                             v
                                                             [:code :confidence :isHeadquarter]
                                                             :confidence
                                                             nil)
      :enrichment-value/list-map (map->table-data l-prefix k v)
      :enrichment-value/list-area-count
      (flatten-name-value-tree->table-data l-prefix k v [:area :count] :count nil)
      :enrichment-value/list-name-score (flatten-name-value-tree->table-data
                                         l-prefix
                                         k
                                         v
                                         [:name :score]
                                         :score
                                         {:score (fn [v]
                                                   (fu/format-with-separators v 1 false 5))})
      :enrichment-value/url-handle-map (fu/sanitize-url (resolve-url-handle v))
      :enrichment-value/phone (fu/sanitize-text (str v))
      :enrichment-value/email (fu/sanitize-text (str v))
      :enrichment-value/timezone (fu/sanitize-text (str v))
      :enrichment-value/offset (fu/sanitize-text (str v))
      :enrichment-value/list-string (fu/sanitize-text (string/join ", " v))
      :enrichment-value/map-list-string
      (some->> v
               (map (fn [[n v]]
                      (when-not (cu/blank-value? v)
                        (str
                         (localize-external-field (str l-prefix "." (name k)) n)
                         ": "
                         (string/join ", " v)))))
               (string/join "\n"))
      :enrichment-value/map-string (some->> v
                                            (map (fn [[n v]]
                                                   (when-not (cu/blank-value? v)
                                                     (str
                                                      (if (keyword? n) (name n) (str n))
                                                      ": "
                                                      v))))
                                            (string/join ", ")
                                            (fu/sanitize-text))
      :enrichment-value/date-str (fu/sanitize-text (str v))
      :enrichment-value/logo [:a
                              {:href   (fu/sanitize-url (str v))
                               :target "_blank"}
                              (loc/tr :logo)]
      :enrichment-value/none nil
      (do
        (logging/info "Not implemented enrichment data : " value-type)
        (fu/sanitize-text (str v))))))

(defn intelligence-table
  [intelligence-entity field-order]
  (let [raw-data (:intelligence-data/raw-data intelligence-entity)
        source (:intelligence-data/source intelligence-entity)
        l-prefix (localization-prefix intelligence-entity)]
    [:table.zap-table.zap-table--bordered.border-top
     [:tbody
      (->> raw-data
           (sort-by (fn [[k v]]
                      (cu/index-of field-order k (count field-order))))
           (keep (fn [[k v]]
                   (let [dom-id (str "intelligence-data-" (name source)
                                     "-entity-" (:intelligence-data/uuid intelligence-entity)
                                     "-" k)
                         label (localize-external-field l-prefix k)
                         value (resolve-value intelligence-entity dom-id k v)]
                     (cond
                       (nil? value)
                       nil
                       (string? value)
                       (when-not (cu/blank-value? value)
                         ^{:key dom-id}
                         [str-value-row label value dom-id])
                       (map? value)
                       ^{:key dom-id}
                       [map-values-row label value dom-id]
                       (and (coll? value)
                            (not (keyword? (first value))))
                       ^{:key dom-id}
                       [table-values-row label value dom-id]
                       :else
                       ^{:key dom-id}
                       [hiccup-value-row label value dom-id]))))
           (doall))]]))

(defn view
  [entities source]
  (let [selected-entity (->> entities
                             (cu/filter-by :intelligence-data/source source)
                             (first))
        entity-type (:intelligence-data/entity-type selected-entity)
        field-order (when entity-type
                      (case entity-type
                        :entity.type/person
                        (case source
                          :clearbit clearbit-person-field-order
                          (do (logging/error "Enrichment view not implemented for person " source)
                              []))
                        :entity.type/company
                        (case source
                          :clearbit clearbit-company-field-order
                          :crunchbase crunchbase-company-field-order
                          (do (logging/error "Enrichment view not implemented for company" source)
                              []))
                        (do (logging/error "Enrichment view not implemented for " entity-type)
                            [])))]
    [:div.mt-3
     {:style {:overflow-x "auto"}}
     (when selected-entity
       [intelligence-table selected-entity field-order])]))
